/**
 * Windows エージェントの配布・登録(AG1)。
 *
 * - 配布物: api イメージに焼き込んだ exe をそのまま落とす(書き換えない)。版と SHA-256 を出す。
 * - 登録コード: 端末ごとのトークンに引き換える使い捨て寄りの合言葉。平文は発行直後だけ見える。
 * - 端末トークン: 端末詳細から失効させる(その 1 台だけ止まる)。
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useState } from "react";

import type { DeviceAgentToken, EnrollCode, EnrollCodeIssued } from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { errorText, useDownload, useInvalidateMdm } from "./hooks";

const { Paragraph, Text } = Typography;

const CODE_STATE: Record<EnrollCode["state"], { label: string; color: string }> = {
  active: { label: "有効", color: "green" },
  expired: { label: "期限切れ", color: "default" },
  used_up: { label: "使い切り", color: "default" },
  revoked: { label: "失効", color: "red" },
};

/** PowerShell の 1 引数として安全に渡すための単一引用符くくり。 */
function psQuote(s: string): string {
  return `'${s.replace(/'/g, "''")}'`;
}

/** 導入コマンド。登録コードは引数に載せず標準入力から貼る(コマンド履歴・プロセス一覧に残さない)。 */
function installCommand(server: string): string {
  return `.\\itmgmt-agent.exe install --server ${psQuote(server)} --enroll-code -`;
}

type IssueForm = { memo: string; ttl_hours: number; max_uses: number };

/** 配布物のダウンロード + 登録コードの発行・一覧 + 導入コマンド。mdm:admin 向け。 */
export function AgentDistCard() {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const download = useDownload();
  const [form] = Form.useForm<IssueForm>();
  const [issued, setIssued] = useState<EnrollCodeIssued | null>(null);
  const [server, setServer] = useState(() => window.location.origin);

  const pkg = useQuery({ queryKey: ["mdm", "agent-package"], queryFn: () => api.agentPackage() });
  const codes = useQuery({ queryKey: ["mdm", "enroll-codes"], queryFn: () => api.enrollCodes() });

  const issue = useMutation({
    mutationFn: (v: IssueForm) => api.issueEnrollCode(v),
    onSuccess: (row) => {
      setIssued(row);
      void message.success("登録コードを発行した(監査ログに記録した)");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  const revoke = useMutation({
    mutationFn: (id: number) => api.revokeEnrollCode(id),
    onSuccess: (row) => {
      if (issued?.id === row.id) setIssued(null);
      const n = row.revoked_tokens ?? 0;
      void message.success(
        n ? `登録コードを失効させた(まだ結び付いていない端末トークン ${n} 件も失効させた)` : "登録コードを失効させた",
      );
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const p = pkg.data;
  return (
    <Card size="small" title="Windows エージェントの配布と登録">
      {pkg.isError && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message="配布物の情報を取得できなかった"
          description={errorText(pkg.error)} />
      )}
      {p && !p.available && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="サーバーにエージェントの配布物が無い"
          description="本番は api イメージを作り直す(AGENT_VERSION を付けて build)。開発は deploy/cypher/build-agent.sh を回す。" />
      )}
      {p?.available && (
        <Descriptions size="small" column={1} style={{ marginBottom: 12 }}
          items={[
            { key: "v", label: "版", children: p.version || <Text type="warning">不明(自己更新は配られない)</Text> },
            { key: "s", label: "SHA-256", children: <Text code copyable>{p.sha256}</Text> },
            {
              key: "g",
              label: "自己更新の署名",
              children: p.signed ? (
                <Tag color="green">署名あり</Tag>
              ) : (
                <Text type="warning">無い(端末は自己更新しない。署名付きの配布物で api イメージを作り直す)</Text>
              ),
            },
            { key: "z", label: "大きさ", children: `${(p.size / 1024 / 1024).toFixed(1)} MB` },
            {
              key: "d",
              label: "ダウンロード",
              children: (
                <Button size="small" onClick={() => void download(p.download_path)}>
                  {p.filename} を落とす
                </Button>
              ),
            },
          ]} />
      )}

      <Typography.Title level={5}>1. 登録コードを発行する</Typography.Title>
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        端末は install のときに登録コードを端末ごとのトークンに引き換える。コードの平文は発行直後だけ表示し、
        サーバーにはハッシュしか残らない。端末トークンは端末詳細から 1 台ずつ失効できる。
        使用回数の既定は 1(1 台に 1 コード)。まとめて入れるときだけ増やす。
        ここで出すコードは台帳に<Text strong>まだ無い</Text>端末用。台帳にある端末(入れ直し・旧方式からの乗り換え)は、
        端末詳細の「入れ直し用の登録コードを発行」で出したコードを使う。
      </Paragraph>
      <Form form={form} layout="inline" initialValues={{ memo: "", ttl_hours: 72, max_uses: 1 }}
        onFinish={(v) => issue.mutate(v)} disabled={issue.isPending} style={{ rowGap: 8, marginBottom: 12 }}>
        <Form.Item name="memo" label="メモ">
          <Input maxLength={200} placeholder="例: 10 月入社分" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="ttl_hours" label="有効期限(時間)" rules={[{ required: true }]}>
          <InputNumber min={1} max={720} />
        </Form.Item>
        <Form.Item name="max_uses" label="使用回数の上限" rules={[{ required: true }]}>
          <InputNumber min={1} max={1000} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={issue.isPending}>発行</Button>
      </Form>

      {issued && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="登録コードが見えるのは今だけ(再表示できない)"
          description={
            <Space direction="vertical" style={{ width: "100%" }}>
              <span>
                登録コード: <Text code copyable={{ text: issued.code }}>{issued.code}</Text>
              </span>
              <span>
                サーバーの URL(端末から届く https の URL):{" "}
                <Input size="small" value={server} onChange={(e) => setServer(e.target.value)}
                  style={{ width: 320 }} />
              </span>
              <Typography.Title level={5} style={{ margin: 0 }}>2. 端末の管理者 PowerShell で実行する</Typography.Title>
              <Text code copyable={{ text: installCommand(server) }}>{installCommand(server)}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                「登録コードを貼り付けて Enter」と聞かれたら、上の登録コードを貼る(コマンドには載せない)。
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                実行前に <Text code>Get-FileHash .\itmgmt-agent.exe -Algorithm SHA256</Text>{" "}
                の値が上の SHA-256 と一致することを確かめる。
              </Text>
            </Space>
          } />
      )}

      <Table<EnrollCode>
        scroll={{ x: "max-content" }}
        size="small"
        rowKey="id"
        loading={codes.isLoading}
        dataSource={codes.data ?? []}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: "登録コードはまだ無い" }}
        columns={[
          { title: "コード", width: 110, dataIndex: "prefix", render: (v: string) => <Text code>{v}…</Text> },
          {
            title: "メモ",
            width: 320,
            ellipsis: true,
            onCell: (row) => ({ title: [row.device_id != null ? `入れ直し(端末 #${row.device_id})` : "", row.memo].filter(Boolean).join(" ") }),
            dataIndex: "memo",
            render: (v: string, r) => (
              <>
                {r.device_id != null && <Tag>入れ直し(端末 #{r.device_id})</Tag>}
                {v}
              </>
            ),
          },
          {
            title: "状態",
            width: 110,
            dataIndex: "state",
            render: (v: EnrollCode["state"]) => <Tag color={CODE_STATE[v].color}>{CODE_STATE[v].label}</Tag>,
          },
          { title: "使用", width: 160, key: "uses", render: (_, r) => `${r.used_count} / ${r.max_uses}` },
          { title: "期限", width: 180, dataIndex: "expires_at", render: (v: string) => jst(v) },
          { title: "発行者", width: 160, dataIndex: "created_by" },
          {
            title: "",
            width: 90,
            key: "ops",
            render: (_, r) =>
              r.state === "active" ? (
                <Popconfirm title="この登録コードを失効させる?(登録済みの端末は止まらない)"
                  onConfirm={() => revoke.mutate(r.id)}>
                  <Button size="small" danger>失効</Button>
                </Popconfirm>
              ) : null,
          },
        ]}
      />
    </Card>
  );
}

/** 登録コードを引き換えたが、まだチェックインしていない(台帳に結び付いていない)端末トークン。 */
export function UnboundAgentTokens() {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const query = useQuery({ queryKey: ["mdm", "unbound-agent-tokens"], queryFn: () => api.unboundAgentTokens() });
  const revoke = useMutation({
    mutationFn: (id: number) => api.revokeAgentToken(id),
    onSuccess: () => {
      void message.success("端末トークンを失効させた");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  if (query.isError) {
    return <Alert type="error" showIcon message="未紐付けの端末トークンを取得できなかった" description={errorText(query.error)} />;
  }
  return (
    <Card size="small" title="まだチェックインしていない端末トークン">
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        登録コードを引き換えたが、まだ台帳の端末に結び付いていないもの。心当たりが無ければ失効させる
        (登録コードを失効させると、そのコードで出したものはまとめて失効する)。
      </Paragraph>
      <Table<DeviceAgentToken>
        scroll={{ x: "max-content" }}
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data ?? []}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: "無い" }}
        columns={[
          { title: "トークン", width: 110, dataIndex: "prefix", render: (v: string) => <Text code>{v}…</Text> },
          { title: "ホスト名(登録時)", width: 200, dataIndex: "hostname" },
          { title: "製造番号(登録時)", width: 180, dataIndex: "serial" },
          { title: "登録", width: 180, dataIndex: "created_at", render: (v: string | null) => jst(v) },
          {
            title: "状態",
            width: 400,
            key: "state",
            render: (_, r) =>
              r.revoked_at ? <Tag color="red">失効({jst(r.revoked_at)} {r.revoked_by})</Tag> : <Tag color="green">有効</Tag>,
          },
          {
            title: "",
            width: 90,
            key: "ops",
            render: (_, r) =>
              !r.revoked_at ? (
                <Popconfirm title="この端末トークンを失効させる?" onConfirm={() => revoke.mutate(r.id)}>
                  <Button size="small" danger>失効</Button>
                </Popconfirm>
              ) : null,
          },
        ]}
      />
    </Card>
  );
}

/**
 * 入れ直し用(端末指定)の登録コード。台帳に既にある端末(OS の入れ直し・旧方式の共有トークンからの
 * 乗り換え)は、このコードでだけ登録し直せる。使用回数は 1、結び付くと古い端末トークンは失効する。
 */
function ReenrollCode({ deviceId }: { deviceId: number }) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const [issued, setIssued] = useState<EnrollCodeIssued | null>(null);
  const issue = useMutation({
    mutationFn: () => api.issueEnrollCode({ memo: "入れ直し", ttl_hours: 72, max_uses: 1, device_id: deviceId }),
    onSuccess: (row) => {
      setIssued(row);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  const command = installCommand(window.location.origin);
  return (
    <Space direction="vertical" style={{ width: "100%", marginBottom: 8 }}>
      <Space wrap>
        <Popconfirm title="この端末の入れ直し用の登録コードを発行する?(登録すると古い端末トークンは失効する)"
          onConfirm={() => issue.mutate()}>
          <Button size="small" loading={issue.isPending}>入れ直し用の登録コードを発行</Button>
        </Popconfirm>
        <Text type="secondary" style={{ fontSize: 12 }}>
          台帳にある端末を入れ直す・旧方式から乗り換えるときに使う(72 時間・1 回きり)
        </Text>
      </Space>
      {issued && (
        <Alert type="warning" showIcon message="登録コードが見えるのは今だけ(再表示できない)"
          description={
            <Space direction="vertical">
              <span>
                登録コード: <Text code copyable={{ text: issued.code }}>{issued.code}</Text>
              </span>
              <Text code copyable={{ text: command }}>{command}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                先に <Text code>.\itmgmt-agent.exe uninstall</Text> してから、管理者の PowerShell で実行し、
                聞かれたら登録コードを貼る。サーバーの URL が端末から届くものと違うときは直す。
              </Text>
            </Space>
          } />
      )}
    </Space>
  );
}

/** 端末詳細の「エージェント」節。この端末に結び付いた端末トークンと失効。 */
export function DeviceAgentTokens({ deviceId, canAdmin }: { deviceId: number; canAdmin: boolean }) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const query = useQuery({
    queryKey: ["mdm", "device-agent-tokens", deviceId],
    queryFn: () => api.deviceAgentTokens(deviceId),
  });
  const revoke = useMutation({
    mutationFn: (id: number) => api.revokeAgentToken(id),
    onSuccess: () => {
      void message.success("端末トークンを失効させた(この端末のチェックインと DLP は止まる)");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  if (query.isError) {
    return <Alert type="error" showIcon message="端末トークンを取得できなかった" description={errorText(query.error)} />;
  }
  return (
    <>
      {canAdmin && <ReenrollCode deviceId={deviceId} />}
      <Table<DeviceAgentToken>
        scroll={{ x: "max-content" }}
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data ?? []}
        pagination={false}
        locale={{ emptyText: "端末トークンは無い(旧方式の共有トークンで動いているか、未登録)" }}
        columns={[
          { title: "トークン", width: 110, dataIndex: "prefix", render: (v: string) => <Text code>{v}…</Text> },
          { title: "登録", width: 180, dataIndex: "created_at", render: (v: string | null) => jst(v) },
          { title: "最終利用", width: 180, dataIndex: "last_used_at", render: (v: string | null) => jst(v) },
          {
            title: "状態",
            width: 400,
            key: "state",
            render: (_, r) =>
              r.revoked_at ? (
                <Tag color="red">失効({jst(r.revoked_at)} {r.revoked_by})</Tag>
              ) : (
                <Tag color="green">有効</Tag>
              ),
          },
          {
            title: "",
            width: 90,
            key: "ops",
            render: (_, r) =>
              canAdmin && !r.revoked_at ? (
                <Popconfirm title="この端末トークンを失効させる?(端末は再登録が要る)"
                  onConfirm={() => revoke.mutate(r.id)}>
                  <Button size="small" danger>失効</Button>
                </Popconfirm>
              ) : null,
          },
        ]}
      />
    </>
  );
}
